import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { cva } from 'class-variance-authority';
import cn from 'classnames';

import { useShuffleArenaOfferMutation } from '@/api/queries/shop';
import { playSound } from '@/common/lib/soundEffect';
import { type ArenaOffersDto } from '@/common/types/shop/marketBoxes';
import { Ticker } from '@/components/common/Ticker/Ticker';
import { ModalContext } from '@/components/Modals/Modal/config';
import { H1, StringSmall } from '@/components/Typography/Typography';
import { GreyBlurSmButton } from '@/components/ui/Button/Button';
import { BuyForTgStarButton } from '@/components/ui/BuyForTgStarButton/BuyForTgStarButton';
// import RadialCircleGradient from '@/components/ui/Gradient/RadialCircleGradient';
import { Picture } from '@/components/ui/Picture/Picture';
import ArenaOfferTimer from '@/components/ui/Timer/ArenaOfferTimer';

import { arenaOffersInfo } from './config';
import RewardArray from './RewardArray';

import css from './style.module.scss';

import superStartImg from '/img/shop/arenaOffers/superStartIcon.svg';
import arrows from '/img/shop/boxes/arrows.svg';

const cardVariants = cva('', {
  variants: {
    variant: {
      default: css.default,
      horizontal: css.horizontal,
    },
  },
});

interface ArenaOfferCardProps extends ArenaOffersDto {
  variant?: 'default' | 'horizontal';
}

const ArenaOfferCard: React.FC<ArenaOfferCardProps> = ({
  variant = 'default',
  ...arenaOffer
}) => {
  const { id, name, reward, cost, discountTimeLeftInSeconds } = arenaOffer;
  const [shuffle, { isLoading }] = useShuffleArenaOfferMutation();
  const { openModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const info = arenaOffersInfo[name];

  const isDiscount: boolean = cost.has_discount;
  const prices = isDiscount ? cost.discounted : cost.base;
  const isSuperStart = name === 'superStart';
  const isWeekend = [
    'growthKitSmall',
    'growthKitBig',
    'growthKitGiant',
  ].includes(name);

  const onBuyOffer = (): void => {
    openModal({
      renderKind: 'buyArenaOffer',
      props: { arenaOffer },
    });
  };

  return (
    <div
      key={id}
      className={cn(
        css.arenaOffer,
        info.color && css[info.color],
        'relative z-[1] flex flex-col gap-2.5 py-3 px-4',
        cardVariants({ variant }),
      )}
    >
      <div className="absolute -top-[10px] -right-[2px]">
        <Ticker
          variant={info.color === 'yellow' ? 'gold' : info.color}
          text={isDiscount ? info.ticker.discount : info.ticker.default}
          classnamestext="text-[14px]"
        />
      </div>
      <div>
        <H1 className="!text-base !leading-none">{t(info.title)}</H1>
        <StringSmall
          className={cn('!text-[0.625rem]', {
            '!text-[#b8ffb6]': info.color === 'green',
            '!text-[#fffdb6]': info.color === 'yellow',
            '!text-[#ffb6b6]': info.color === 'red',
            '!text-[#E7D7FF]': info.color === 'purple',
          })}
        >
          {t(info.describe)}
        </StringSmall>
      </div>
      <RewardArray variant="small" name={name} reward={reward} offerId={id} />
      <div
        className={cn(
          'flex justify-between relative z-[5] !h-8',
          isWeekend && 'mt-[25px]',
        )}
      >
        {!isWeekend && (
          <GreyBlurSmButton
            className="h-full !w-[40%]"
            text={t('Shuffle')}
            isLoading={isLoading}
            classNamesText="gap-2"
            onClick={() => {
              playSound('buttons/shuffle');
              shuffle({ id, body: reward });
            }}
          >
            <img src={arrows} alt="arrows" className="size-[0.75rem]" />
          </GreyBlurSmButton>
        )}
        <div className="relative h-full !w-[45%]">
          {isDiscount && (
            <ArenaOfferTimer
              text={isSuperStart ? 'for 1-6 lvl' : undefined}
              value={discountTimeLeftInSeconds}
              variant="top"
            />
          )}
          <BuyForTgStarButton
            className="h-full !w-full"
            price={isDiscount && !isSuperStart ? cost.base.usd : undefined}
            discountPercentage={
              isDiscount && !isSuperStart ? cost.displayDiscountPct : undefined
            }
            discountPrice={prices.usd}
            wrapperClassName="w-full h-full"
            onClick={onBuyOffer}
          />
        </div>
      </div>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-[8px] bg-[#000] z-[-2]">
        {isSuperStart && (
          <div className="absolute top-[17%] right-[7%] z-[2] w-[80px] h-[80px]">
            <img src={superStartImg} className="w-full h-full" alt="icon" />
            <StringSmall className="absolute top-1/2 -translate-y-1/2 !font-[900] !text-xs !leading-none w-full text-center rotate-[15deg]">
              {cost.displayDiscountPct} %<br /> VALUE
            </StringSmall>
          </div>
        )}
        <Picture
          img={info.img.png}
          webp={info.img.webp}
          className={cn(
            css.imgShadow,
            'absolute top-0 h-full aspect-ratio-1/1 z-[1] [&>img]:object-cover',
          )}
          alt="banner"
        />
        {/* <RadialCircleGradient
          type={info.color}
          className="bottom-[-50%] left-[50%] -translate-x-1/2 w-[50%] aspect-ratio-1/1 opacity-[0.5] !backdrop-blur-0 !blur-0 z-[2]"
        />
        <RadialCircleGradient
          type={info.color}
          className="top-[-50%] left-[-50%] translate-x-1/2 w-[50%] aspect-ratio-1/1 opacity-[0.5] !backdrop-blur-0 !blur-0 z-[2]"
        />
        <RadialCircleGradient
          type={info.color}
          className="top-[-50%] right-[-50%] -translate-x-1/2 w-[50%] aspect-ratio-1/1 opacity-[0.5] !backdrop-blur-0 !blur-0 z-[2]"
        /> */}
      </div>
    </div>
  );
};

export default ArenaOfferCard;
